/**
 * useChat — the chat surface's React-facing hook. Bridges the WS-driven
 * Zustand slice (`useChatStream`) and TanStack Query's `sendChatMessage`
 * mutation, exposing a stable React contract:
 *
 *   const {
 *     messages,             // user + assistant turns (assistant turns are
 *                           //   snapshots of the streaming buffer once it
 *                           //   transitions to a new message)
 *     streamingByAgentId,   // keyed map of in-flight streaming buffers
 *     isStreaming,          // any agent is currently streaming or running a tool
 *     currentAgents,        // agents not in 'idle' phase — for the active-agents UI
 *     runShape,             // 'single' | 'parallel' | 'sequential' | 'hierarchical'
 *     sendMessage,          // (text, options?) => void — POST /chat/message
 *     stopGeneration,       // closes the in-flight turn (best-effort)
 *     errorBanner,          // user-safe error string, or null
 *   } = useChat({ sessionId });
 *
 * Streaming render path: ChatPanel selects `messages` from this hook (changes
 * only when an assistant message FINALIZES) and a separate `StreamingRows`
 * component (rendering one `AgentStreamingRow` per active agent) subscribes to
 * `useChatStream(s => s.streamingByAgentId)` directly so the per-token
 * re-renders never reach the virtualized list.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useChatStream } from '@/stores/chatStream.store';
import type {
  AgentRuntime,
  StreamingBufferMap,
} from '@/stores/chatStream.store';
import type { AgentId, AgentPhase } from '@/services/ws/types';
import type { ToolInvocation } from '@/components/molecules/ToolInvocationChip';
import { getSession, sendChatMessage } from '@/api/generated';
import type {
  ChatMessageRequest,
  ChatMessageResponse,
} from '@/api/generated/types.gen';
import type { AttachmentMeta } from '@/components/organisms/ChatComposer';
import { ApiError } from '@/api/client';
import { useAgentActivity } from '@/stores/agentActivity.store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RunShape = 'single' | 'parallel' | 'sequential' | 'hierarchical';

/**
 * Inline card payload attached to a chat message. Phase 3 introduced these
 * to render Full-Auto task surfaces directly in the chat thread without
 * inventing a separate message stream. When `card` is set the renderer
 * skips the regular MessageBubble and renders the matching molecule.
 */
export type ChatMessageCard =
  | {
      readonly kind: 'task.ack';
      readonly runId: string;
      readonly title: string;
      readonly agent: AgentId;
      readonly packageName: string;
    }
  | {
      readonly kind: 'task.live';
      readonly runId: string;
    }
  | {
      readonly kind: 'task.terminal';
      readonly runId: string;
      readonly title: string;
      readonly outcome: 'success' | 'warning' | 'failure';
      readonly summary: string;
    }
  | {
      readonly kind: 'fullauto.confirm';
      readonly packageName: string;
      readonly summary: string;
      readonly originalPrompt: string;
      readonly estimatedDurationMin: number;
    };

export interface ChatMessage {
  readonly id: string;
  readonly role: 'user' | 'agent' | 'system';
  readonly agent?: AgentId;
  readonly content: string;
  readonly timestamp: string;
  readonly tools?: readonly ToolInvocation[];
  readonly errored?: boolean;
  /** Agent IDs that contributed to a parallel multi-agent response. */
  readonly contributingAgents?: readonly string[];
  /** Inline card payload — when set, the renderer surfaces a card-shaped
   *  Phase 3 molecule (ack/live/terminal/fullauto.confirm) instead of a
   *  text bubble. */
  readonly card?: ChatMessageCard;
}

export interface UseChatOptions {
  readonly sessionId: string;
  /** Bindle id to scope the request — passed straight through to the API. */
  readonly bindleId?: string;
}

export interface UseChatResult {
  readonly messages: readonly ChatMessage[];
  readonly streamingByAgentId: StreamingBufferMap;
  readonly isStreaming: boolean;
  readonly currentAgents: readonly AgentRuntime[];
  readonly runShape: RunShape;
  readonly errorBanner: string | null;
  readonly sendMessage: (
    text: string,
    options?: { agent?: AgentId; attachments?: readonly AttachmentMeta[] },
  ) => Promise<void>;
  readonly stopGeneration: () => void;
  readonly clearError: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACTIVE_PHASES: ReadonlySet<AgentPhase> = new Set([
  'thinking',
  'tool',
  'streaming',
]);

/** Derive the run shape from the set of currently-active agents. The S2
 * UX only needs a coarse hint; the detailed strategy lives server-side. */
const RUN_SHAPES: ReadonlySet<RunShape> = new Set([
  'single',
  'parallel',
  'sequential',
  'hierarchical',
]);
function isRunShape(value: string): value is RunShape {
  return (RUN_SHAPES as ReadonlySet<string>).has(value);
}

function deriveRunShape(active: readonly AgentRuntime[]): RunShape {
  if (active.length <= 1) return 'single';
  // If many agents are simultaneously in 'streaming' or 'tool' we're parallel;
  // otherwise we treat it as sequential. 'hierarchical' is only set explicitly
  // by the server today (over `agent.message.metadata.executionStrategy`); we
  // expose it here for completeness via `setRunShape` from the API response.
  const concurrentlyActive = active.filter((a) =>
    a.phase === 'streaming' || a.phase === 'tool',
  ).length;
  return concurrentlyActive > 1 ? 'parallel' : 'sequential';
}

let messageIdCounter = 0;
function nextLocalId(prefix: string): string {
  messageIdCounter += 1;
  return `${prefix}-${Date.now()}-${messageIdCounter}`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useChat(options: UseChatOptions): UseChatResult {
  const { sessionId, bindleId } = options;
  const queryClient = useQueryClient();

  // Local message log. User messages appear immediately; assistant messages
  // are snapshotted from `streaming` when its `messageId` transitions.
  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [explicitRunShape, setExplicitRunShape] =
    useState<RunShape | null>(null);

  // ---- history loader -------------------------------------------------------
  // Load existing messages from the server for persisted sessions. Ephemeral
  // playground sessions (prefixed 'play-') are never persisted.
  const isEphemeral = sessionId.startsWith('play-');
  const historyQuery = useQuery({
    queryKey: ['session', sessionId],
    queryFn: async () => {
      const result = await getSession({ path: { sessionId } });
      if (result.error !== undefined || !result.data) {
        throw new ApiError('Failed to load session history', 500);
      }
      return result.data;
    },
    enabled: !isEphemeral,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Don't retry failed history loads — the user can still chat on a fresh
    // session even if history is unavailable. Avoids blocking the first render
    // with cascading retry delays.
    retry: false,
  });

  // Seed message state from server history on first successful load.
  // Reset the seeded flag when sessionId changes so a new session's history
  // is loaded correctly.
  const historySeededRef = useRef(false);
  const lastSeededSessionRef = useRef<string | null>(null);
  if (lastSeededSessionRef.current !== sessionId) {
    lastSeededSessionRef.current = sessionId;
    historySeededRef.current = false;
  }
  useEffect(() => {
    if (historySeededRef.current || !historyQuery.data) return;
    const msgs = historyQuery.data.messages;
    if (!msgs || !Array.isArray(msgs) || msgs.length === 0) return;
    historySeededRef.current = true;
    const loaded: ChatMessage[] = msgs
      .filter((m) => m.role !== 'tool') // tool messages are not rendered in chat
      .map((m) => {
        // Map backend role values to the frontend's ChatMessage role enum.
        // Backend uses 'assistant' for agent messages; frontend calls them 'agent'.
        const role: ChatMessage['role'] =
          m.role === 'assistant' ? 'agent'
          : m.role === 'system' ? 'system'
          : 'user';

        // The backend message ID may be a composite key (e.g. `msg#timestamp#uuid`).
        // Ensure we always have a stable string ID for React keys and de-dupe checks.
        const id = m.id || `history-${m.timestamp}-${Math.random().toString(36).slice(2, 8)}`;

        const base: ChatMessage = {
          id,
          role,
          content: m.content,
          timestamp: m.timestamp,
        };
        if (m.agentId) return { ...base, agent: m.agentId as AgentId };
        return base;
      });
    setMessages(loaded);
  }, [historyQuery.data]);

  // Drain finalized buffers into the message list whenever they appear.
  // The store's drainFinalized() is the canonical way to consume completed
  // streaming buffers; we call it on every change to finalizedQueue.
  const streamingByAgentId = useChatStream((s) => s.streamingByAgentId);
  const finalizedQueue = useChatStream((s) => s.finalizedQueue);
  const agents = useChatStream((s) => s.agents);

  useEffect(() => {
    // Drain finalized entries and add them as completed messages.
    if (finalizedQueue.length === 0) return;
    const drained = useChatStream.getState().drainFinalized();
    for (const entry of drained) {
      if (entry.buffer.text.length === 0) continue;
      const snapshot: ChatMessage = {
        id: entry.buffer.messageId,
        role: 'agent',
        agent: entry.buffer.agentId,
        content: entry.buffer.text,
        // M6: stamp with the server-derived finalize timestamp (from event.ts)
        // rather than the browser wallclock — single clock domain.
        timestamp: new Date(entry.finalizedAtTs).toISOString(),
        errored: entry.buffer.errored || entry.reason === 'error' || entry.reason === 'stop',
      };
      setMessages((prior) =>
        // de-dupe defensively — a re-mount could race the snapshot.
        prior.some((m) => m.id === snapshot.id)
          ? prior
          : [...prior, snapshot],
      );
    }
  }, [finalizedQueue]);

  const currentAgents = useMemo<readonly AgentRuntime[]>(() => {
    const out: AgentRuntime[] = [];
    for (const id in agents) {
      const a = agents[id as AgentId];
      if (a && ACTIVE_PHASES.has(a.phase)) out.push(a);
    }
    return out;
  }, [agents]);

  const runShape: RunShape = useMemo(
    () => explicitRunShape ?? deriveRunShape(currentAgents),
    [explicitRunShape, currentAgents],
  );

  const isStreaming = currentAgents.length > 0 || Object.values(streamingByAgentId).some(Boolean);

  // ---- mutation -----------------------------------------------------------

  const mutation = useMutation<
    ChatMessageResponse,
    Error,
    { text: string; agent?: AgentId; attachments?: readonly AttachmentMeta[] }
  >({
    mutationFn: async ({ text, agent, attachments }) => {
      const body: ChatMessageRequest & { attachments?: readonly AttachmentMeta[] } = {
        message: text,
        sessionId,
        ...(bindleId !== undefined ? { bindleId } : {}),
        ...(agent !== undefined ? { role: agent } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      };
      const result = await sendChatMessage({ body });
      // The generated client returns `{ data, error, response }`. ApiError
      // is thrown by the response interceptor for non-2xx, so `error` should
      // be populated only for explicit-throw configurations (off here).
      if (result.error !== undefined) {
        throw new ApiError('Request failed', 500);
      }
      if (!result.data) {
        throw new ApiError('Empty response', 500);
      }
      return result.data;
    },
    onSuccess: (data) => {
      const md = data.metadata;
      if (md?.executionStrategy && isRunShape(md.executionStrategy)) {
        setExplicitRunShape(md.executionStrategy);
      }

      // Invalidate session list so sidebar picks up renamed title
      void queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] });

      // Async dispatch: the server dispatched the agent asynchronously.
      // Tokens may arrive via WebSocket (chatStream.store) or we poll DynamoDB
      // as fallback. Polling stops as soon as a response is found from either source.
      if ((data as any).status === 'streaming') {
        if (data.role) {
          useAgentActivity.getState().markActive(data.role);
        }

        const knownIds = new Set(messages.map((m) => m.id));
        let pollCount = 0;
        const pollInterval = setInterval(async () => {
          pollCount++;
          if (pollCount > 20) {
            clearInterval(pollInterval);
            return;
          }
          // If WS already delivered a streamed message, stop polling
          const streamMap = useChatStream.getState().streamingByAgentId;
          const hasActiveStream = Object.values(streamMap).some(
            (buf) => buf && buf.text.length > 0,
          );
          if (hasActiveStream) {
            clearInterval(pollInterval);
            return;
          }
          try {
            const result = await getSession({ path: { sessionId } });
            if (result.data?.messages) {
              const newMsgs = result.data.messages.filter(
                (m) => m.role === 'assistant' && !knownIds.has(m.id ?? ''),
              );
              for (const m of newMsgs) {
                const id = m.id || `poll-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                knownIds.add(id);
                const msg: ChatMessage = {
                  id,
                  role: 'agent',
                  agent: (m.agentId || data.role || 'developer') as AgentId,
                  content: m.content,
                  timestamp: m.timestamp || new Date().toISOString(),
                };
                setMessages((prior) =>
                  prior.some((p) => p.id === id) ? prior : [...prior, msg],
                );
              }
              if (newMsgs.length > 0) {
                clearInterval(pollInterval);
              }
            }
          } catch {
            // Polling is best-effort
          }
        }, 2000);
        return;
      }

      // Surface memory system hints as system messages
      const extMeta = (data as Record<string, unknown>).metadata as Record<string, unknown> | undefined;
      if (extMeta?.coldStart) {
        const hoursAway = (extMeta.hoursAway as number) ?? 0;
        const welcomeMsg = hoursAway > 48
          ? 'Welcome back! I remember our previous conversations and have loaded relevant context.'
          : 'Welcome back! I have context from your recent sessions.';
        setMessages((prev) => [
          ...prev,
          {
            id: `system-coldstart-${Date.now()}`,
            role: 'system' as const,
            content: welcomeMsg,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
      if (extMeta?.correctionsApplied) {
        setMessages((prev) => [
          ...prev,
          {
            id: `system-correction-${Date.now()}`,
            role: 'system' as const,
            content: 'I\'ve applied your previous feedback to this response.',
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      // Multi-agent async dispatch: coordinator was fired asynchronously.
      // Mark target agents as active, show dispatch message, then poll for
      // agent responses until they arrive in DynamoDB.
      const status = (data as Record<string, unknown>).status;
      if (status === 'dispatched') {
        const targets: string[] = ((data as Record<string, unknown>).targetAgents as string[] | undefined) || [];
        if (targets.length > 0) {
          for (const agentId of targets) {
            useAgentActivity.getState().markActive(agentId as AgentId);
          }
        }
        setExplicitRunShape('parallel');

        // Poll for agent responses (coordinator persists them to DynamoDB)
        const knownIds = new Set(messages.map((m) => m.id));
        let pollCount = 0;
        const pollInterval = setInterval(async () => {
          pollCount++;
          if (pollCount > 12) {
            clearInterval(pollInterval);
            setExplicitRunShape(null);
            return;
          }
          try {
            const result = await getSession({ path: { sessionId } });
            if (result.data?.messages) {
              const newMsgs = result.data.messages.filter(
                (m) => m.role === 'assistant' && m.agentId && !knownIds.has(m.id ?? ''),
              );
              for (const m of newMsgs) {
                const id = m.id || `poll-${m.timestamp}-${Math.random().toString(36).slice(2, 8)}`;
                knownIds.add(id);
                const msg: ChatMessage = {
                  id,
                  role: 'agent',
                  agent: m.agentId as AgentId,
                  content: m.content,
                  timestamp: m.timestamp || new Date().toISOString(),
                };
                setMessages((prior) =>
                  prior.some((p) => p.id === id) ? prior : [...prior, msg],
                );
                // Mark this agent as completed
                useAgentActivity.getState().markActive(m.agentId as AgentId);
              }
              // Stop polling once we have responses from all targets
              if (newMsgs.length >= targets.length) {
                clearInterval(pollInterval);
                setExplicitRunShape(null);
              }
            }
          } catch {
            // Polling is best-effort
          }
        }, 3000);
      }

      // When WS streaming is unavailable, the REST response is the only
      // source of the agent's reply. Add it as a finalized message so it
      // renders in the chat panel.
      if (data.response) {
        // Multi-agent attribution: when multiple agents contributed in parallel,
        // extract their IDs so ChatPanel can render an expandable disclosure.
        const contributingAgentIds =
          data.agentContributions && data.agentContributions.length > 0
            ? data.agentContributions.map((c) => c.agentId)
            : undefined;

        const agentMsg: ChatMessage = {
          id: nextLocalId('agent'),
          role: 'agent',
          content: data.response,
          timestamp: new Date().toISOString(),
          ...(data.role ? { agent: data.role } : {}),
          ...(contributingAgentIds ? { contributingAgents: contributingAgentIds } : {}),
        };
        setMessages((prior) => [...prior, agentMsg]);
      }
      // Notify the agent-activity store so the RightPanel can show a brief
      // "working" indicator even when the WebSocket is unavailable.
      if (data.role) {
        useAgentActivity.getState().markActive(data.role);
      }
    },
    onError: (err) => {
      setErrorBanner(
        err instanceof ApiError && err.status === 429
          ? 'Rate limited. Please slow down.'
          : 'Something went wrong. Please retry.',
      );
    },
  });

  // ---- API ----------------------------------------------------------------

  const sendMessage = useCallback(
    async (
      text: string,
      opts?: { agent?: AgentId; attachments?: readonly AttachmentMeta[] },
    ): Promise<void> => {
      const trimmed = text.trim();
      const hasAttachments = (opts?.attachments?.length ?? 0) > 0;
      if (trimmed.length === 0 && !hasAttachments) return;

      // Build the user message shown in the UI — append an attachment summary
      const attachSuffix = hasAttachments
        ? '\n\n📎 ' + opts!.attachments!.map((a) => a.name).join(', ')
        : '';
      const userMsg: ChatMessage = {
        id: nextLocalId('user'),
        role: 'user',
        content: (trimmed || '(attachment)') + attachSuffix,
        timestamp: new Date().toISOString(),
      };
      setMessages((prior) => [...prior, userMsg]);
      setErrorBanner(null);
      setExplicitRunShape(null);
      const args: { text: string; agent?: AgentId; attachments?: readonly AttachmentMeta[] } = {
        text: trimmed || '(attachment)',
        ...(opts?.agent !== undefined ? { agent: opts.agent } : {}),
        ...(hasAttachments ? { attachments: opts!.attachments } : {}),
      };
      await mutation.mutateAsync(args).catch(() => {
        // onError already populated the banner; swallow to keep send fire-and-forget.
      });
    },
    [mutation],
  );

  const stopGeneration = useCallback((): void => {
    // Best-effort stop. We can't tell the server "abort" without a dedicated
    // endpoint; what we CAN do is force-finalize all active streaming buffers
    // locally so the UI stops following along, and let the server's next
    // `agent.phase: success` close the books. The reducer will resume
    // tracking when the next `agent.message` arrives.
    //
    // Read directly from the store (not a stale ref) so we always see the
    // live buffer map at the moment the user clicks Stop.
    const store = useChatStream.getState();
    const current = store.streamingByAgentId;
    for (const agentId of Object.keys(current) as AgentId[]) {
      if (current[agentId]) {
        store.forceFinalize(agentId, 'stop');
      }
    }
  }, []);

  const clearError = useCallback((): void => setErrorBanner(null), []);

  return {
    messages,
    streamingByAgentId,
    isStreaming,
    currentAgents,
    runShape,
    errorBanner,
    sendMessage,
    stopGeneration,
    clearError,
  };
}
